import { useEffect, useState } from 'react';

// Chrome/Edge/Android fire `beforeinstallprompt` once, early. Capture it at
// import time so any component can offer an "Install app" action later.
let deferredPrompt = null;
const listeners = new Set();
const notify = () => listeners.forEach((fn) => fn());

if (typeof window !== 'undefined') {
    window.addEventListener('beforeinstallprompt', (event) => {
        event.preventDefault();
        deferredPrompt = event;
        notify();
    });
    window.addEventListener('appinstalled', () => {
        deferredPrompt = null;
        notify();
    });
}

export const isStandalone = () =>
    typeof window !== 'undefined'
    && (window.matchMedia?.('(display-mode: standalone)').matches || window.navigator.standalone === true);

const isIos = () =>
    typeof navigator !== 'undefined'
    && /iphone|ipad|ipod/i.test(navigator.userAgent);

// Returns { canInstall, isIosManual, install }.
//  - canInstall: the browser can show its native install dialog now.
//  - isIosManual: iOS Safari has no install API; show "Share > Add to Home Screen".
export function usePwaInstall() {
    const [, force] = useState(0);

    useEffect(() => {
        const fn = () => force((n) => n + 1);
        listeners.add(fn);
        return () => listeners.delete(fn);
    }, []);

    const install = async () => {
        if (!deferredPrompt) return false;
        deferredPrompt.prompt();
        const choice = await deferredPrompt.userChoice;
        deferredPrompt = null;
        notify();
        return choice?.outcome === 'accepted';
    };

    const installed = isStandalone();
    return {
        canInstall: !installed && Boolean(deferredPrompt),
        isIosManual: !installed && isIos() && !deferredPrompt,
        install,
    };
}
